import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Plus } from 'lucide-react';
import type { ReviewProfileView } from '@autopilot/contracts';
import { api } from '../../api/client';
import { qk } from '../../api/hooks';
import { EmptyState, ErrorAlert, QueryBoundary } from '../../components/States';
import { ToneBadge } from '../../components/StatusBadge';
import { formatDateTime } from '../../lib/format';

export function ReviewProfilesTab() {
  const [edit, setEdit] = useState<ReviewProfileView | 'new' | null>(null);
  const q = useQuery({ queryKey: qk.reviewProfiles, queryFn: api.listReviewProfiles });
  const models = useQuery({ queryKey: qk.models, queryFn: api.listModels });
  const modelName = (id: string) => models.data?.find((m) => m.id === id)?.name ?? '–';
  const items = q.data ?? [];
  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 820, flex: 1 }}>
          Ein Prüfprofil legt die beiden unabhängigen Prüfer fest (Richtigkeit und Fehlerrisiko). Sie erhalten getrennte Aufträge und sehen weder die Selbsteinschätzung des Generators noch das Urteil des anderen Prüfers. Änderungen erzeugen eine neue Version.
        </Typography>
        <Button variant="contained" startIcon={<Plus size={18} />} onClick={() => setEdit('new')}>
          Prüfprofil anlegen
        </Button>
      </Box>
      <Card>
        <QueryBoundary query={q} action="Prüfprofile laden" loadingRows={3}>
          {items.length === 0 ? (
            <EmptyState title="Noch kein Prüfprofil" description="Ohne aktives Prüfprofil kann kein Pre-Heating gestartet werden." />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table aria-label="Prüfprofile" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Richtigkeitsprüfer</TableCell>
                    <TableCell>Fehlerprüfer</TableCell>
                    <TableCell>Promptversionen</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Version {p.version} · {formatDateTime(p.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>{modelName(p.correctnessModelConfigId)}</TableCell>
                      <TableCell>{modelName(p.errorRiskModelConfigId)}</TableCell>
                      <TableCell>
                        <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                          {p.promptVersions.generator} · {p.promptVersions.correctness} · {p.promptVersions.errorRisk}
                        </Typography>
                      </TableCell>
                      <TableCell>{p.active ? <ToneBadge tone="success" label="Aktiv" /> : <ToneBadge tone="neutral" label="Inaktiv" />}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => setEdit(p)}>
                          Bearbeiten
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </QueryBoundary>
      </Card>
      {edit && <ProfileDialog profile={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />}
    </>
  );
}

function ProfileDialog({ profile, onClose }: { profile: ReviewProfileView | null; onClose: () => void }) {
  const id = useId();
  const qc = useQueryClient();
  const models = useQuery({ queryKey: qk.models, queryFn: api.listModels });
  const [name, setName] = useState(profile?.name ?? '');
  const [corr, setCorr] = useState(profile?.correctnessModelConfigId ?? '');
  const [risk, setRisk] = useState(profile?.errorRiskModelConfigId ?? '');
  const [active, setActive] = useState(profile?.active ?? true);
  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), correctnessModelConfigId: corr, errorRiskModelConfigId: risk, active };
      return profile ? api.updateReviewProfile(profile.id, body) : api.createReviewProfile(body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.reviewProfiles });
      onClose();
    },
  });
  const byRole = (r: 'CORRECTNESS' | 'ERROR_RISK') => (models.data ?? []).filter((m) => m.roles.includes(r));
  return (
    <Dialog open onClose={onClose} aria-labelledby={id} maxWidth="sm" fullWidth>
      <DialogTitle id={id}>{profile ? 'Prüfprofil bearbeiten' : 'Prüfprofil anlegen'}</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2.5 }}>
        {profile && <Alert severity="info">Speichern erzeugt Version {profile.version + 1}. Gesellschaftsregeln bleiben auf ihrer Version, bis sie bewusst aktualisiert werden.</Alert>}
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <TextField select label="Richtigkeitsprüfer" value={corr} onChange={(e) => setCorr(e.target.value)}>
          {byRole('CORRECTNESS').map((m) => (
            <MenuItem key={m.id} value={m.id} disabled={!m.active}>
              {m.name} ({m.modelId})
            </MenuItem>
          ))}
        </TextField>
        <TextField select label="Fehlerprüfer" value={risk} onChange={(e) => setRisk(e.target.value)}>
          {byRole('ERROR_RISK').map((m) => (
            <MenuItem key={m.id} value={m.id} disabled={!m.active}>
              {m.name} ({m.modelId})
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />} label="Aktiv" />
        {save.error ? <ErrorAlert error={save.error} action="Prüfprofil speichern" /> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={!name.trim() || !corr || !risk || save.isPending}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}
